import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { BaseChartData, ChartDayData, TableKind, TimeFrame, VersionView } from 'types/analytics'
import { formatUTCDay, unixToType } from 'utils/analytics/date'

export const toNumber = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === '') return 0
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

export const dedupeIds = (ids?: string[]) => {
  if (!ids) return undefined

  const seen = new Set<string>()
  const deduped: string[] = []

  ids.forEach((id) => {
    if (!id) return
    const lower = id.toLowerCase()
    if (!seen.has(lower)) {
      seen.add(lower)
      deduped.push(lower)
    }
  })

  return deduped
}

export const buildSelectionKey = (ids?: string[]) => {
  if (ids === undefined) return 'top'
  if (ids.length === 0) return 'empty'
  return ids.slice().sort().join('-')
}

type FormatOptions = {
  isLatest?: boolean
  now?: Dayjs
}

export const formatTimeframeLabel = (anchor: Dayjs, timeframe: TimeFrame, options?: FormatOptions) => {
  const { isLatest = false, now = dayjs() } = options ?? {}

  if (timeframe === 'D') {
    return anchor.format('MMM D, YYYY')
  }

  const start = timeframe === 'W' ? anchor.startOf('week') : anchor.startOf('month')
  const nominalEnd = timeframe === 'W' ? start.add(6, 'day') : start.endOf('month')
  const end = isLatest && nominalEnd.isAfter(now) ? now : nominalEnd
  const sameYear = start.year() === end.year()
  const sameMonth = start.month() === end.month()

  const startFormat = sameYear ? 'D MMM' : 'D MMM YYYY'
  const endFormat = sameYear && sameMonth ? 'D MMM' : sameYear ? 'D MMM' : 'D MMM YYYY'

  return `${start.format(startFormat)} - ${end.format(endFormat)}`
}

// apr chart

dayjs.extend(utc)

export type AprChartPoint = BaseChartData & { label: string }

const TIMEFRAME_DAY_COUNT: Record<TimeFrame, number> = {
  D: 1,
  W: 7,
  M: 30,
}

export const TIMEFRAME_OPTIONS: TimeFrame[] = ['D', 'W', 'M']

const calculateAprValue = (fees: number, tvl: number, timeframe: TimeFrame) => {
  if (!Number.isFinite(fees) || !Number.isFinite(tvl) || tvl <= 0) return 0

  const days = TIMEFRAME_DAY_COUNT[timeframe]
  if (!days) return 0

  return (fees / tvl) * (365 / days) * 100
}

const buildAprChartDataForTimeframe = (data: ChartDayData[], timeframe: TimeFrame): AprChartPoint[] => {
  if (!data.length) return []

  if (timeframe === 'D') {
    return data.map((entry) => {
      const anchor = dayjs.unix(entry.date).utc()
      return {
        time: formatUTCDay(entry.date),
        value: calculateAprValue(entry.feesUSD ?? 0, entry.tvlUSD ?? 0, timeframe),
        label: formatTimeframeLabel(anchor, 'D'),
      }
    })
  }

  const grouped = new Map<
    string,
    { fees: number; tvlSum: number; entries: number; latestUnix: number; lastTvl: number }
  >()

  data.forEach((entry) => {
    const key = timeframe === 'W' ? unixToType(entry.date, 'week') : unixToType(entry.date, 'month')
    const fees = entry.feesUSD ?? 0
    const tvl = entry.tvlUSD ?? 0

    const existing = grouped.get(key)
    if (existing) {
      existing.fees += fees
      existing.tvlSum += tvl
      existing.entries += 1
      if (entry.date > existing.latestUnix) {
        existing.latestUnix = entry.date
        existing.lastTvl = tvl
      }
    } else {
      grouped.set(key, {
        fees,
        tvlSum: tvl,
        entries: 1,
        latestUnix: entry.date,
        lastTvl: tvl,
      })
    }
  })

  const now = dayjs()

  return Array.from(grouped.values())
    .sort((a, b) => a.latestUnix - b.latestUnix)
    .map((bucket, index, arr) => {
      const anchor = dayjs.unix(bucket.latestUnix).utc()
      const isLatest = index === arr.length - 1
      const averageTvl = bucket.entries > 0 ? bucket.tvlSum / bucket.entries : bucket.lastTvl

      return {
        time: formatUTCDay(bucket.latestUnix),
        value: calculateAprValue(bucket.fees, averageTvl, timeframe),
        label: formatTimeframeLabel(anchor, timeframe, { isLatest, now }),
      }
    })
}

export const buildAprChartSeries = (data: ChartDayData[]) => {
  return {
    D: buildAprChartDataForTimeframe(data, 'D'),
    W: buildAprChartDataForTimeframe(data, 'W'),
    M: buildAprChartDataForTimeframe(data, 'M'),
  }
}

export const buildAnalyticsPath = (version: VersionView, tableView: TableKind) => {
  return `/explore/${version}/${tableView}`
}
